import React from "react";
import { DraftOrderDetail } from "../hooks/useDraft";
import { useUser } from "src/shared/context/UserContext";
import CommonButtons from "src/shared/components/CommonButtons";
import { User, Receipt } from "lucide-react";

interface OrderSummaryPanelProps {
  draft: DraftOrderDetail;
  billNo?: string;
  receivedAmount?: string;
  onStatusChange?: (status: string) => void;
  onCancelOrder?: () => void;
  onPrintReceipt?: () => void;
  onReviewOrder?: () => void;
}

const formatMoney = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toFixed(2);
};

export default function OrderSummaryPanel({ draft, billNo, receivedAmount, onReviewOrder }: OrderSummaryPanelProps) {
  const { user } = useUser();

  // ✅ Hide Review Approval for User role
  const isUserRole = user?.role?.[0] === "User";

  const customerName = [draft.customerDetails.firstName, draft.customerDetails.middleName, draft.customerDetails.lastName].filter(Boolean).join(" ") || "Walk-in Customer";

  const displayReceived = receivedAmount ?? draft.payments?.receivedAmount;

  // ✅ Reusable row — OrderPriceDetails style
  const renderDetail = (label: string, value: string | number) => (
    <div className="flex justify-between px-2.5 my-2">
      <h2 className="text-slate-500">{label}</h2>
      <h2 className="font-semibold text-slate-800 tabular-nums">{value}</h2>
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-1.5 mb-1 transition-all shadow-md">
      {/* ── Customer Identity Section ─────────────────────────── */}
      <div className="flex items-center gap-4 mb-1 pb-1 border-b border-slate-50">
        <div className="flex-shrink-0 w-10 h-10 bg-slate-900 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-slate-200">
          <User className="w-5 h-5" />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">Customer Name</span>
          <h2 className="text-xl font-black text-slate-800 tracking-tight leading-tight truncate">{customerName}</h2>
          {draft.customerDetails.phoneNumber && <span className="text-xs text-slate-500 mt-0.5">{draft.customerDetails.phoneNumber}</span>}
        </div>
      </div>

      {/* ── Metadata Row (Order ID & Bill No) ─────────────────── */}
      <div className="flex items-center justify-between px-2 py-1 bg-slate-50 border border-slate-100 rounded-xl shadow-sm mb-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">OrderId :</span>
          <span className="text-sm font-bold text-slate-800">{draft.orderId}</span>
        </div>

        {billNo && (
          <>
            <div className="h-4 w-[1px] bg-slate-200 mx-2" />
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Bill No. :</span>
              <span className="text-sm font-bold text-slate-800">{billNo}</span>
            </div>
          </>
        )}
      </div>

      {/* ── Payment Details Section ───────────────────────────── */}
      <div className="bg-slate-50 border border-slate-100 rounded-xl shadow-sm">
        {/* Section header */}
        <div className="flex items-center gap-2 px-2.5 pt-2 pb-1 border-b border-slate-100">
          <Receipt className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">Payment Details</span>
        </div>

        {/* Rows — OrderPriceDetails style */}
        <div className="py-1">
          {renderDetail("Sub total", formatMoney(draft.orderAmount))}
          {renderDetail("Discount (-)", formatMoney(draft.discount))}
          {renderDetail("Delivery Fee (+)", formatMoney(draft.deliveryFee))}
          {renderDetail("Total Amount", formatMoney(draft.finalAmount))}
          {renderDetail("Received Amount", formatMoney(displayReceived))}
          {renderDetail("Remaining balance", formatMoney(draft.remainingAmount))}
          {renderDetail("Outstanding balance", formatMoney(draft.outstandingBalance))}
          {renderDetail("Payment mode", draft.payments?.paymentMethod ?? "—")}
          {renderDetail("Order date & time", `${draft.orderDate} & ${draft.orderTime}`)}
        </div>
      </div>

      {/* ── Review Approval (hidden for User role) ────────────── */}
      {draft.approvalStatus === "Pending" && !isUserRole && (
        <div className="mt-1">
          <CommonButtons label="REVIEW APPROVAL" onClick={onReviewOrder} className="w-full px-4 py-2.5" />
        </div>
      )}
    </div>
  );
}
